import { useEffect, useState, type RefObject } from 'react';

interface Options {
  /** Passed straight to IntersectionObserver. */
  rootMargin?: string;
  threshold?: number | number[];
  /** Stop observing after the first time the element is seen. */
  once?: boolean;
}

/**
 * Whether the referenced element is in the viewport. Falls back to `true`
 * where IntersectionObserver does not exist (jsdom, very old browsers) so
 * nothing that depends on it stays hidden.
 */
export default function useInView<T extends Element>(
  ref: RefObject<T | null>,
  { rootMargin = '0px', threshold = 0, once = false }: Options = {},
): boolean {
  const supported = typeof window !== 'undefined' && typeof IntersectionObserver !== 'undefined';
  const [inView, setInView] = useState<boolean>(!supported);

  useEffect(() => {
    if (!supported) return undefined;
    const node = ref.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (entry.isIntersecting) {
          setInView(true);
          if (once) observer.disconnect();
        } else if (!once) {
          setInView(false);
        }
      },
      { rootMargin, threshold },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref, rootMargin, threshold, once, supported]);

  return inView;
}
